/* eslint-disable no-restricted-imports */
import { RoutePath, StringSlash } from '@app/routes';
import { Icon } from '@components/Icon';
import { Tooltip } from '@components/Tooltip';
import { useFlagMe498644EnableHoverCopyLinkButtonForHyperlinks } from '@generated/flags/ME-498644-enable-hover-copy-link-button-for-hyperlinks';
import { t } from '@lingui/core/macro';
import { win } from '@utils/win';
import cx from 'classnames';
import { ReactElement, useEffect, useRef, useState } from 'react';
import {
  Link as RawLink,
  LinkProps as RawLinkProps,
  useHref,
} from 'react-router-dom';
import {
  copyLinkButtonStyle,
  copyLinkIconContainerStyle,
  copyLinkIconHiddenStyle,
  copyLinkIconStyle,
  copyLinkIconVisibleStyle,
  copyLinkTooltipLabelStyle,
  copyLinkTooltipTextHiddenStyle,
  copyLinkTooltipTextStyle,
  copyLinkTooltipTextVisibleStyle,
  linkContainerStyle,
  linkStyle,
} from './style.css';

const COPIED_STATE_DURATION_MS = 2000;

type CopyFeedbackState = 'idle' | 'copied' | 'dismissed';

export type OptionalSearch = `?${string}` | '';

export interface LinkProps<RoutePathType extends string>
  extends Omit<RawLinkProps, 'to'> {
  hideCopyBtn?: boolean;
  to: `${RoutePathType}${OptionalSearch}`;
}

export type MMRoutePathType = RoutePath<StringSlash, StringSlash>;

/** MasterMind specific links */
export type MMLinkTo = `${MMRoutePathType}${OptionalSearch}`;

export const GenericLink = <RoutePathType extends string>({
  ...props
}: LinkProps<RoutePathType>): ReactElement => {
  return <RawLink {...props} />;
};

export const Link = ({
  hideCopyBtn = false,
  ...props
}: LinkProps<RoutePath<StringSlash, StringSlash>>): ReactElement => {
  const isHoverCopyLinkButtonEnabled =
    useFlagMe498644EnableHoverCopyLinkButtonForHyperlinks();
  const href = useHref(props.to);
  const [copyFeedbackState, setCopyFeedbackState] =
    useState<CopyFeedbackState>('idle');
  const resetTimerRef = useRef(0);
  const copyButtonRef = useRef<HTMLButtonElement>(null);
  const isCopied = copyFeedbackState === 'copied';
  const isDismissed = copyFeedbackState === 'dismissed';
  const copyLinkLabel = t`Copy link`;
  const copiedLabel = t`Copied!`;
  const tooltipLabel = isCopied ? copiedLabel : copyLinkLabel;

  const resetDismissedFeedback = (): void => {
    if (isDismissed) {
      setCopyFeedbackState('idle');
    }
  };

  useEffect(() => {
    return (): void => {
      win.clearTimeout(resetTimerRef.current);
    };
  }, []);

  if (!isHoverCopyLinkButtonEnabled || hideCopyBtn) {
    return (
      <>
        <GenericLink {...props} className={cx(linkStyle, props.className)} />
      </>
    );
  }

  return (
    <Tooltip
      interactive
      hoverDelay={1000}
      positionType="topStart"
      label={
        <button
          ref={copyButtonRef}
          type="button"
          aria-label={tooltipLabel}
          className={copyLinkButtonStyle}
          data-testid="copy-link-button"
          onFocus={resetDismissedFeedback}
          onClick={(event): void => {
            event.preventDefault();
            event.stopPropagation();
            const absoluteUrl = new URL(href, win.location.href).href;
            void win.navigator.clipboard.writeText(absoluteUrl);
            win.clearTimeout(resetTimerRef.current);
            setCopyFeedbackState('copied');
            resetTimerRef.current = win.setTimeout(() => {
              copyButtonRef.current?.blur();
              setCopyFeedbackState('dismissed');
            }, COPIED_STATE_DURATION_MS);
          }}
        >
          <span className={copyLinkIconContainerStyle} aria-hidden>
            <Icon
              i="copy"
              size="xs"
              color="primary"
              className={cx(
                copyLinkIconStyle,
                copyFeedbackState === 'idle'
                  ? copyLinkIconVisibleStyle
                  : copyLinkIconHiddenStyle,
              )}
              data-testid="copy-link-copy-icon"
            />
            <Icon
              i="checkCircle"
              size="xs"
              color="success"
              className={cx(
                copyLinkIconStyle,
                copyFeedbackState === 'copied'
                  ? copyLinkIconVisibleStyle
                  : copyLinkIconHiddenStyle,
              )}
              data-testid="copy-link-success-icon"
            />
          </span>
          <span className={copyLinkTooltipLabelStyle}>
            <span
              className={cx(
                copyLinkTooltipTextStyle,
                isCopied
                  ? copyLinkTooltipTextHiddenStyle
                  : copyLinkTooltipTextVisibleStyle,
              )}
            >
              {copyLinkLabel}
            </span>
            <span
              className={cx(
                copyLinkTooltipTextStyle,
                isCopied
                  ? copyLinkTooltipTextVisibleStyle
                  : copyLinkTooltipTextHiddenStyle,
              )}
            >
              {copiedLabel}
            </span>
          </span>
        </button>
      }
      ariaLabel={tooltipLabel}
      isVisible={isCopied || undefined}
      hidden={isDismissed}
      data-testid="copy-link-popover"
      css={{
        fontSize: 10,
        padding: '2px 6px',
      }}
    >
      <span
        className={linkContainerStyle}
        data-testid="link-with-copy-container"
        onMouseEnter={resetDismissedFeedback}
        onFocus={resetDismissedFeedback}
        data-hyperlink-container
      >
        <GenericLink {...props} className={cx(linkStyle, props.className)} />
      </span>
    </Tooltip>
  );
};
